import React, { useEffect, useState } from "react";
import { toast } from "sonner";
import { AlertTriangle, ArrowRightLeft, Loader2 } from "lucide-react";
import { Button } from "@/components/ui/button";
import { api } from "../lib/api";

/**
 * FT-4 — dependency conflicts are FLAGGED; "Shift successors" is the one click that runs the
 * existing cascade (locks respected, logged). Nothing moves without it. Renders nothing while the
 * Settings switch is off.
 */
export default function DepConflictsBar({ projectId, onShifted }) {
  const [data, setData] = useState(null);
  const [busy, setBusy] = useState("");
  const load = async () => {
    try { setData((await api.get(`/scheduling/projects/${projectId}/dep-conflicts`)).data); } catch { setData(null); }
  };
  useEffect(() => { load(); /* eslint-disable-next-line */ }, [projectId]);
  if (!data?.enabled || !data.conflicts.length) return null;

  const shift = async (ids) => {
    setBusy(ids.length ? ids[0] : "all");
    try {
      const r = await api.post("/scheduling/deps/shift", { project_id: projectId, dep_ids: ids, view: "ra" });
      toast.success(`${r.data.shifted.length} successor(s) moved`, { description: "Logged in the schedule audit" });
      await load();
      onShifted?.();
    } catch (e) { toast.error(e.response?.data?.detail || "Nothing moved"); }
    finally { setBusy(""); }
  };

  return (
    <div className="rounded-lg border border-amber-300 bg-amber-50 px-3 py-2 text-xs space-y-1" data-testid="dep-conflicts-bar">
      <div className="flex items-center gap-2">
        <AlertTriangle className="w-4 h-4 text-amber-700" />
        <span className="font-semibold text-amber-900">
          {data.conflicts.length} dependency conflict{data.conflicts.length === 1 ? "" : "s"} — successors are not at predecessor + offset
        </span>
        <Button size="sm" className="ml-auto h-7 text-xs bg-amber-600 hover:bg-amber-700" disabled={!!busy}
                onClick={() => shift([])} data-testid="dep-shift-all-btn">
          {busy === "all" ? <Loader2 className="w-3.5 h-3.5 mr-1 animate-spin" /> : <ArrowRightLeft className="w-3.5 h-3.5 mr-1" />}
          Shift all successors
        </Button>
      </div>
      <ul className="space-y-0.5">
        {data.conflicts.map((c) => (
          <li key={c.dep_id} className="flex items-center gap-2 text-amber-900" data-testid={`dep-conflict-${c.dep_id}`}>
            <span className="truncate">
              <b>{c.succ}</b> is {c.succ_date || "unset"} — should be <b>{c.expected}</b> ({c.pred} {c.pred_date} + {c.offset_days} wd)
            </span>
            <button type="button" className="ml-auto underline shrink-0" disabled={!!busy} onClick={() => shift([c.dep_id])}
                    data-testid={`dep-shift-${c.dep_id}`}>
              Shift
            </button>
          </li>
        ))}
      </ul>
    </div>
  );
}
